/* eslint-disable react-hooks/set-state-in-effect, react-hooks/refs -- appraiser state-machine effects and stable callback refs coordinate the multi-step workflow. */
import React, { Fragment, useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getAppraiserUsedTmvPath } from 'client/data/utils/used-tmv-experiment';
import { Row, Col } from 'reactstrap';
import classnames from 'classnames';
import { isEmpty, noop, uniq, compact } from 'lodash-es';

// Data and model
import { connect } from 'react-redux';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { VehicleModel, VehicleEntities } from 'client/data/models/vehicle-v2';
import { buildStylesBasicPathFromVin, VehicleVinModel } from 'client/data/models/vehicle-vin';
import { PartnerOfferModel, buildPartnerOfferVehicleInfoPath } from 'client/data/models/partner-offer-model';
import { sellYourCarConfigParams } from 'client/data/models/dealer-info';

// Utils
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { Storage } from 'site-modules/shared/utils/storage';
import { updateVehicleByStyleId } from 'client/actions/vehicle';
import { validateZipCode } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { isEqualById } from 'site-modules/shared/utils/appraiser/compare-utils';
import { getQuery } from 'client/utils/location';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { fireQuoteRecencyTrackingPixel } from 'site-modules/shared/utils/appraisal/common-utils';
import { isTruckVehicle } from 'site-modules/shared/utils/is-truck-vehicle';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { logger } from 'client/utils/logger/logger';
import { transformTmvConditions } from 'client/site-modules/shared/utils/appraiser/luckdragon-utils';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import {
  getStepTitle,
  shouldRenderTestimonials,
  getPredefinedVin,
  getTotalPrices,
  getModsCreationPayload,
  getTransmissionData,
  getRenewalState,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-utils';
import {
  fireAbTestBucketing,
  fireSubmodelContainsCab,
  fireAppraisalPrefillTracking,
  fireOfferExpirationRecencyTracking,
} from 'site-modules/shared/components/appraisal/trade-in-appraiser/utils/trade-in-appraiser-tracking-utils';

// Constants
import {
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  TRADE_IN_VALUE,
  CONDITIONS,
  INSTANT_OFFER,
} from 'site-modules/shared/constants/appraisal/appraisal';

// Components
import { AppraisalReportUserTestimonials } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/appraisal-report-step/appraisal-report-step-user-testimonials';

import 'site-modules/shared/components/appraisal/appraisal-common.scss';

import { useAppraiserScroll } from './hooks/use-appraiser-scroll';
import { useVehicleData } from './hooks/use-vehicle-data';
import { useVinManagement } from './hooks/use-vin-management';
import { useSelectionHandlers } from './hooks/use-selection-handlers';
import { useStepNavigation } from './hooks/use-step-navigation';
import { useEmailReport } from './hooks/use-email-report';
import { useIdmPersistence } from './hooks/use-idm-persistence';
import { TradeInAppraiserContextProvider } from './trade-in-appraiser-context';
import { TradeInAppraiserHeader } from './trade-in-appraiser-header';
import { TradeInAppraiserItem } from './trade-in-appraiser-item';
import { TradeInAppraiserStep } from './trade-in-appraiser-step';
import { getDefaultState, validateAndUpdateStep } from './trade-in-appraiser-step-processor';
import {
  getLocationAndStyleData,
  getOptionsAndColorsData,
  getConditionAndMileageData,
  getAppraisalReportData,
  getLicensePlateAndVinData,
  getOfferConditionsData,
  getEmailStepData,
  getInstantOfferData,
  getEscapeHatchData,
} from './trade-in-appraiser-step-data';
import { isLocationAndStyleStepValid, checkStepsCompleteness } from './trade-in-appraiser-validators';

import './trade-in-appraiser.scss';

const APPRAISAL_ESTIMATE_CREATIVE_ID = 'edm-entry-appraisal-estimate';

/**
 * Trade-In Appraiser.
 */
export function TradeInAppraiserUI({
  setModelValue,
  className = '',
  zipCode: propsZipCode = '',
  isAuthenticated = false,
  isMobile = false,
  onDone = null,
  updateVehicleWithGtm = noop,
  columnsSize = {
    xs: 12,
    md: 11,
    lg: 9,
    xl: 8,
  },
  isExtendedReport = false,
  useGraphqlUsedTmv = false,
  tmvConditionsResponseData = {
    tmvConditions: null,
    responseId: undefined,
  },
  estimatedVehicle = null,
  headerText = 'How much is your car worth?',
  subHeaderText = 'Complete the form to generate an appraisal report',
  onEstimatedValuesWidgetHide = null,
  onPartnerFaqQuestionsShow = noop,
  setAppraiseFinished = null,
  userEmail = '',
  location,
  isUnknownVisitorZip = false,
  vehicleOverride = null,
  onLinkWidgetHide = noop,
  vinStyles = null,
  drawerCreativeId = null,
  modsData = {},
  isContrastUpdate = false,
  isAppExtensionPage = false,
  scrollToOnApply = undefined,
  dma = null,
  ReportStep = null,
  LocationAndStyleStepComponent,
  creativeIdOverride = undefined,
  onTradeInUpdate = null,
  onVehicleUpdate = null,
  sellingPrice = undefined,
  msrp = undefined,
  residualValue = undefined,
  isPurchase = undefined,
  sellYourCarConfig = {},
  isUsed = false,
  displayTradeInValues = true,
  params = {},
  isHighlightsPage = false,
  isEmo4318Chal1 = false,
  isHiddenForRenewal = false,
  renewalValues = null,
  renewalModsRecord = null,
  emo4379Recipe = null,
  ...restProps
}) {
  const isRenewal = Boolean(renewalValues && renewalModsRecord?.vehicle);
  const [isRenewalSetupReady, setIsRenewalSetupReady] = useState(!isRenewal);
  const props = {
    ...restProps,
    setModelValue,
    className,
    zipCode: propsZipCode,
    isAuthenticated,
    isMobile,
    onDone,
    updateVehicleWithGtm,
    columnsSize,
    isExtendedReport,
    useGraphqlUsedTmv,
    tmvConditionsResponseData,
    estimatedVehicle,
    headerText,
    subHeaderText,
    onEstimatedValuesWidgetHide,
    onPartnerFaqQuestionsShow,
    setAppraiseFinished,
    userEmail,
    location,
    isUnknownVisitorZip,
    vehicleOverride,
    onLinkWidgetHide,
    vinStyles,
    drawerCreativeId,
    modsData,
    isContrastUpdate,
    isAppExtensionPage,
    scrollToOnApply,
    dma,
    ReportStep,
    LocationAndStyleStepComponent,
    creativeIdOverride,
    isEmo4318Chal1,
    isHiddenForRenewal,
    onTradeInUpdate,
    onVehicleUpdate,
    sellingPrice,
    msrp,
    residualValue,
    isPurchase,
    sellYourCarConfig,
    isUsed,
    displayTradeInValues,
    params,
    isHighlightsPage,
    renewalValues: isRenewal ? renewalValues : null,
    renewalModsRecord: isRenewal ? renewalModsRecord : null,
    emo4379Recipe,
    isRenewalSetupReady,
  };

  // --- State ---
  const [state, setState] = useState(() => {
    const defaultState = getDefaultState({
      zipCode: isUnknownVisitorZip ? '' : propsZipCode,
      selectedStyle: {},
      location,
      isUnknownVisitorZip,
      isZipCodeValid: true,
      isExtendedReport,
      predefinedVin: getPredefinedVin(location, true),
      modsData,
    });
    if (!isRenewal) return defaultState;
    return getRenewalState(defaultState, estimatedVehicle, renewalModsRecord.vehicle, renewalValues);
  });
  const [transitioningStepId, setTransitioningStepId] = useState(null);
  const [isNextStepOpenDelayed, setIsNextStepOpenDelayed] = useState(false);

  // --- Refs (instance variables) ---
  const storageRef = useRef(null);
  const vehicleIdRef = useRef(null);
  const lastSavedStyleIdRef = useRef(null);
  const mileageRef = useRef(null);
  const prevPropsRef = useRef({
    tmvConditionsResponseId: tmvConditionsResponseData?.responseId,
    zipCode: propsZipCode,
    vehicleOverride,
    modsData,
  });
  const prevStateRef = useRef(state);
  // Stable ref always pointing at the current state — lets hooks read latest state without it being a dep
  const stateRef = useRef(state);
  stateRef.current = state;
  // Pending callbacks queue - replaces setState(newState, callback) pattern
  const pendingCallbacksRef = useRef([]);
  // Use ref for changeStep so updateVinAndContinueLpAndVin can call it without circular deps
  const changeStepRef = useRef(null);

  // Flush pending callbacks after state updates (replaces setState callback pattern)
  useEffect(() => {
    if (pendingCallbacksRef.current.length > 0) {
      const callbacks = pendingCallbacksRef.current;
      pendingCallbacksRef.current = [];
      callbacks.forEach(cb => cb());
    }
  });

  // Clear pending callbacks on unmount to prevent firing after unmount
  useEffect(
    () => () => {
      pendingCallbacksRef.current = [];
    },
    []
  );

  // --- Helpers ---
  const getActiveStep = useCallback(() => state.steps.find(step => step.isActive), [state.steps]);

  // --- Scroll methods ---
  const {
    scrollTo,
    scrollToNextStep,
    scrollToReportStep,
    scrollToTradeInTitle,
    scrollToInvalidWithMap,
    debouncedScrollToStep,
    smoothScrollToStep,
  } = useAppraiserScroll({ isExtendedReport, isMobile, getActiveStep });

  // --- Luckdragon model updates ---
  const updateAllTmvParams = useCallback(async () => {
    const { vehicle, mileage, exteriorColor, condition, options, zipCode } = state;
    const optionIds = options.map(option => option.id).join(',');

    await setModelValue('allTmvParams', VehicleModel, {
      styleId: vehicle?.style.id,
      condition,
      mileage,
      zipCode,
      optionIds,
      colorId: exteriorColor?.id,
    });
  }, [state, setModelValue]);

  const updateLocationZip = useCallback(
    zipCode => setModelValue('location', VisitorModel, { zipCode }),
    [setModelValue]
  );

  // --- Zip code validation ---
  const setValidatedZipStatus = useCallback(
    (isZipCodeValidVal, cb = noop) => {
      setState(prev => {
        const newState = { ...prev, isZipCodeValid: isZipCodeValidVal };
        return validateAndUpdateStep(
          newState,
          LOCATION_AND_STYLE,
          isLocationAndStyleStepValid,
          { isExtendedReport },
          { resetSubsequent: false }
        );
      });
      cb();
    },
    [isExtendedReport]
  );

  const changeZipCode = useCallback(
    value => {
      if (value === state.zipCode) return;

      delayHeavyTask(() => {
        setState(prev => ({ ...prev, zipCode: value }));

        if (validateZipCode(value)) {
          updateLocationZip(value)
            .then(() => setValidatedZipStatus(true))
            .catch(() => setValidatedZipStatus(false));
        } else {
          setValidatedZipStatus(false);
        }
      });
    },
    [state.zipCode, updateLocationZip, setValidatedZipStatus]
  );

  // --- Update error map and scroll ---
  const updateErrorMapAndScrollToInvalid = useCallback(
    validationErrors => {
      setState(prev => ({ ...prev, errorMap: validationErrors }));
      // Defer scroll so React renders the error elements before we query the DOM
      setTimeout(() => {
        scrollToInvalidWithMap(validationErrors);
      }, 0);
    },
    [scrollToInvalidWithMap]
  );

  // --- Custom hooks ---
  const {
    getVehicleData,
    getVehicleStyle,
    getVehicleYear,
    getVehicleMMY,
    setGeneralVehicleData,
    setNewStyle,
    setAdditionalStepsData,
  } = useVehicleData({
    state,
    setState,
    location,
    isExtendedReport,
    mileageRef,
    pendingCallbacksRef,
    setModelValue,
  });

  const {
    updateOfferParametersVin,
    changeVin,
    clearVin,
    clearVinEscapeHatch,
    updateVinAndContinueEscapeHatch,
    checkVinMismatchError,
    checkVinStylesMismatchError,
    toggleLicensePlateAndVinStepDone,
    updateVinAndContinueLpAndVin,
  } = useVinManagement({
    state,
    setState,
    setModelValue,
    vinStyles,
    estimatedVehicle,
    scrollToNextStep,
    pendingCallbacksRef,
    changeStepRef,
  });

  useEffect(() => {
    if (isRenewal) {
      Promise.all([updateLocationZip(renewalValues.zipCode), updateAllTmvParams(), updateOfferParametersVin()])
        .then(() => setIsRenewalSetupReady(true))
        .catch(error => logger.warn(error, 'Failed to initialize appraisal renewal'));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- initialize the renewed funnel once from its initial state
  }, []);

  const {
    selectCondition,
    selectOptions,
    selectExteriorColor,
    selectInteriorColor,
    changeMileage,
    onMileageBlur,
    updateQuestionnaireResponses,
  } = useSelectionHandlers({ setState, isExtendedReport, stateRef });

  const {
    saveAppraiserDataToIdm,
    saveMultiOfferAndInstantOfferDataToIdm,
    saveEstimatedPayments,
    setPartnerOfferVinEligibilityWithSave,
  } = useIdmPersistence({
    setState,
    props,
    vehicleIdRef,
    lastSavedStyleIdRef,
    storageRef,
    getVehicleMMY,
    tmvConditionsResponseData,
    isExtendedReport,
    isAuthenticated,
    pendingCallbacksRef,
    stateRef,
  });

  const { resetEmailReportStatus } = useEmailReport({ stateRef, setState, userEmail });

  // --- Loading colors and options ---
  const setLoadingColorsAndOptions = useCallback(isLoading => {
    setState(prev => ({ ...prev, isLoadingColorsAndOptions: isLoading }));
  }, []);

  // --- Set step loaded ---
  const setIsOfferConditionsStepLoaded = useCallback(() => {
    setState(prev => ({ ...prev, isOfferConditionsStepLoaded: true }));
  }, []);

  const setIsLicensePlateAndVinStepLoaded = useCallback(() => {
    setState(prev => ({ ...prev, isLicensePlateAndVinStepLoaded: true }));
  }, []);

  const setIsInstantOfferStepLoaded = useCallback(() => {
    setState(prev => ({ ...prev, isInstantOfferStepLoaded: true }));
  }, []);

  // --- Various setters ---
  const setDecodedVinEligibility = useCallback(decodedVinEligibility => {
    setState(prev => ({ ...prev, decodedVinEligibility }));
  }, []);

  const clearDecodedVinEligibility = useCallback(() => setDecodedVinEligibility(null), [setDecodedVinEligibility]);

  const setChromeStyleIds = useCallback(chromeStyleIds => {
    setState(prev => ({ ...prev, chromeStyleIds }));
  }, []);

  const setTranslationTransmissionType = useCallback(
    ({ transmissionType, isOverride = false, isSelectedByUser = false }) => {
      setState(prev => ({
        ...prev,
        transmissionType,
        isTransmissionTypeTranslationOverride: isOverride,
        isTransmissionTypeSelectedByUser: isSelectedByUser,
      }));
    },
    []
  );

  const setTranslationEngine = useCallback(({ engine, isOverride = false }) => {
    setState(prev => ({
      ...prev,
      engine,
      isEngineSizeTranslationOverride: isOverride,
    }));
  }, []);

  const setHasExteriorColorsOptions = useCallback(hasExteriorColorsOptions => {
    setState(prev => ({ ...prev, hasExteriorColorsOptions }));
  }, []);

  const resetOnColorsAndOptionsLoaded = useCallback(() => {
    setState(prev => ({ ...prev, onColorsAndOptionsLoaded: null }));
  }, []);

  const getMileageRef = useCallback(ref => {
    mileageRef.current = ref;
  }, []);

  const handlePayoffChange = useCallback((amountValue, appliedTradeIn) => {
    setState(prev => ({ ...prev, payoffAmount: amountValue, appliedTradeIn }));
  }, []);

  const handlePaymentLimitsLoading = useCallback(isLoading => {
    setState(prev => ({ ...prev, isPaymentLimitsLoading: isLoading }));
  }, []);

  // --- Question response getters ---
  const getAllQuestionResponses = useCallback(
    () => state.partnerQuestionnaireVerboseResponse,
    [state.partnerQuestionnaireVerboseResponse]
  );

  // --- MODS creation payload ---
  const getModsCreationPayloadLocal = useCallback(
    ({ vehicleFeatures, conditionPricing, abTestBucketing }) => {
      const {
        condition,
        vehicle,
        exteriorColor,
        interiorColor,
        engine,
        chromeStyleIds,
        vin,
        mileage,
        zipCode,
        options,
        transmissionType: transmissionTypeFromState,
        isTransmissionTypeSelectedByUser,
      } = state;

      const { transmissionType } = getTransmissionData({
        vinStyles,
        transmissionType: transmissionTypeFromState,
        isTransmissionTypeSelectedByUser,
      });

      return getModsCreationPayload({
        vehicleFeatures,
        conditionPricing,
        abTestBucketing,
        dma,
        condition,
        vehicle,
        exteriorColor,
        interiorColor,
        transmissionType,
        engine,
        chromeStyleIds,
        vin,
        mileage,
        zipCode,
        options,
      });
    },
    [state, vinStyles, dma]
  );

  const { toggleStepDone, changeStep, onStepCollapseComplete } = useStepNavigation({
    state,
    setState,
    getVehicleStyle,
    getVehicleYear,
    getActiveStep,
    isExtendedReport,
    isMobile,
    location,
    updateAllTmvParams,
    updateOfferParametersVin,
    onEstimatedValuesWidgetHide,
    onLinkWidgetHide,
    onPartnerFaqQuestionsShow,
    pendingCallbacksRef,
    changeStepRef,
    debouncedScrollToStep,
    smoothScrollToStep,
    scrollToReportStep,
    scrollToTradeInTitle,
    updateErrorMapAndScrollToInvalid,
    stateRef,
    isEmo4318Chal1,
    setTransitioningStepId,
    setIsNextStepOpenDelayed,
  });

  // Keep changeStepRef up to date
  changeStepRef.current = changeStep;

  // --- Helpers object for step data getters (provided via TradeInAppraiserContext) ---
  const helpers = {
    changeStep,
    changeZipCode,
    setGeneralVehicleData,
    setAdditionalStepsData,
    changeVin,
    clearVin,
    clearVinEscapeHatch,
    updateVinAndContinueEscapeHatch,
    updateVinAndContinueLpAndVin,
    updateErrorMapAndScrollToInvalid,
    checkVinMismatchError,
    selectOptions,
    selectExteriorColor,
    selectInteriorColor,
    setLoadingColorsAndOptions,
    setTranslationTransmissionType,
    setTranslationEngine,
    setChromeStyleIds,
    setHasExteriorColorsOptions,
    resetOnColorsAndOptionsLoaded,
    changeMileage,
    selectCondition,
    getMileageRef,
    onMileageBlur,
    getVehicleData,
    toggleStepDone,
    handlePayoffChange,
    handlePaymentLimitsLoading,
    resetEmailReportStatus,
    checkStepsCompleteness: () => checkStepsCompleteness(stateRef.current.steps),
    scrollTo,
    scrollToNextStep,
    setDecodedVinEligibility,
    clearDecodedVinEligibility,
    getActiveStep,
    updateQuestionnaireResponses,
    toggleLicensePlateAndVinStepDone,
    setIsOfferConditionsStepLoaded,
    setIsLicensePlateAndVinStepLoaded,
    setIsInstantOfferStepLoaded,
    getModsCreationPayload: getModsCreationPayloadLocal,
    saveMultiOfferAndInstantOfferDataToIdm,
    saveAppraiserDataToIdm,
    getAllQuestionResponses,
    setNewStyle,
    updateAllTmvParams,
    setPartnerOfferVinEligibility: setPartnerOfferVinEligibilityWithSave,
  };

  // --- componentDidMount ---
  useEffect(() => {
    storageRef.current = new Storage('localStorage');
    const { zipCode: stateZipCode, vin } = state;

    if (drawerCreativeId) {
      setModelValue('appraisalDrawerApiResponseCreativeId', VehicleModel, drawerCreativeId);
    }

    setValidatedZipStatus(validateZipCode(stateZipCode));

    if (isExtendedReport && estimatedVehicle && !isRenewal) {
      const style = state?.vehicle?.style;
      const preselectedStyle = isEmpty(style) ? null : style;
      setGeneralVehicleData(
        estimatedVehicle.make,
        estimatedVehicle.model,
        { year: estimatedVehicle.year.toString() },
        preselectedStyle,
        true
      );
    }

    if (estimatedVehicle && vin) {
      checkVinStylesMismatchError();
    }

    fireAppraisalPrefillTracking(modsData);
    fireQuoteRecencyTrackingPixel(modsData.offerFinalizedAt);
    fireAbTestBucketing(modsData);
    fireOfferExpirationRecencyTracking(location, modsData);

    if (isExtendedReport && isTruckVehicle(estimatedVehicle)) {
      fireSubmodelContainsCab(estimatedVehicle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- componentDidMount: must run once on mount only
  }, []);

  // --- Private helpers that use state ---
  function isChangedOnlyStyle() {
    const { vehicle } = state;
    const prevVehicle = prevStateRef.current?.vehicle;
    if (prevVehicle && !isEqualById(prevVehicle, vehicle)) {
      const {
        make: { slug: makeSlug },
        model: { slug: modelSlug },
        year,
      } = estimatedVehicle;

      const vehicleMake = vehicle?.make?.slug;
      const vehicleModel = vehicle?.model?.slug;
      const vehicleYear = vehicle?.year?.year;

      return (
        uniq(compact([makeSlug, modelSlug, `${year}`, vehicleMake, vehicleModel, vehicleYear])).length === 3 &&
        vehicle.style
      );
    }
    return false;
  }

  function fillParamsFromLastQuote() {
    const {
      mileage: modsMileage,
      condition: modsCondition,
      transmission = '',
      exteriorColorId,
      interiorColorId,
      selectedOptions = [],
    } = modsData;

    const lastQuoteMileage = modsMileage ? `${modsMileage}` : undefined;
    const {
      condition: queryCondition = '',
      colorId: queryExteriorColorId,
      interiorColorId: queryInteriorColorId,
    } = getQuery(location);

    delayHeavyTask(() => {
      setState(prev => {
        const prefilledOptions = isEmpty(prev.prefilledOptions) ? selectedOptions : prev.prefilledOptions;
        return {
          ...prev,
          mileage: prev.mileage || lastQuoteMileage,
          condition: queryCondition ? CONDITIONS[queryCondition.toUpperCase()] : modsCondition || prev.condition,
          transmissionTypeFromLastQuote: prev.transmissionType || transmission.toUpperCase(),
          prefilledExteriorColorId: prev.isStyleFromStyleMismatchModal
            ? null
            : prev.prefilledExteriorColorId || exteriorColorId || vinStyles?.exteriorColor?.id || queryExteriorColorId,
          prefilledInteriorColorId: prev.isStyleFromStyleMismatchModal
            ? null
            : prev.prefilledInteriorColorId || interiorColorId || vinStyles?.interiorColor?.id || queryInteriorColorId,
          prefilledOptions: prev.isStyleFromStyleMismatchModal ? [] : prefilledOptions,
        };
      });
    });
  }

  function onVehicleOverride(vehicleOverrideParam, creativeIdOverrideParam) {
    const { make, model, year, style } = vehicleOverrideParam;
    setGeneralVehicleData(make, model, year, style, true, () => {
      // Read active step from latest state via setState to avoid stale closure
      setState(prev => {
        const activeStep = prev.steps.find(step => step.isActive);
        const { nextId } = activeStep;
        const callback = () =>
          changeStepRef.current({
            stepId: nextId,
            creativeId: creativeIdOverrideParam || APPRAISAL_ESTIMATE_CREATIVE_ID,
          });
        if (nextId === COLORS_AND_OPTIONS) {
          return { ...prev, onColorsAndOptionsLoaded: callback };
        }
        // Queue changeStep to fire after this state update
        pendingCallbacksRef.current.push(callback);
        return prev;
      });
    });
  }

  // --- componentDidUpdate: modsData changes ---
  useEffect(() => {
    const prevMods = prevPropsRef.current.modsData;
    const hasModsDataChanged = modsData?.vin !== prevMods?.vin || (isEmpty(prevMods) && !isEmpty(modsData));

    if (hasModsDataChanged) {
      fireAppraisalPrefillTracking(modsData);
      fireAbTestBucketing(modsData);
      fireOfferExpirationRecencyTracking(location, modsData);
    }

    const isOnStepOne = getActiveStep()?.id === LOCATION_AND_STYLE;

    if (
      !isEmpty(modsData) &&
      state.vehicle.style &&
      (hasModsDataChanged || isChangedOnlyStyle()) &&
      (isOnStepOne || state.isStyleFromStyleMismatchModal)
    ) {
      fillParamsFromLastQuote();
    }

    prevPropsRef.current.modsData = modsData;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when modsData or vehicle changes; other deps are read but should not trigger the effect
  }, [modsData, state.vehicle]);

  // --- componentDidUpdate: tmvConditions response ---
  useEffect(() => {
    if (isHiddenForRenewal || !isRenewalSetupReady) return;

    const prevResponseId = prevPropsRef.current.tmvConditionsResponseId;
    const currentResponseId = tmvConditionsResponseData?.responseId;

    if (currentResponseId !== prevResponseId) {
      saveAppraiserDataToIdm();
    }

    prevPropsRef.current.tmvConditionsResponseId = currentResponseId;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- save when responseId changes and renewal is visible and initialized
  }, [tmvConditionsResponseData?.responseId, isHiddenForRenewal, isRenewalSetupReady]);

  // --- componentDidUpdate: zipCode props change ---
  useEffect(() => {
    const prevZip = prevPropsRef.current.zipCode;
    if (prevZip !== propsZipCode && state.zipCode !== propsZipCode) {
      setState(prev => ({ ...prev, zipCode: propsZipCode }));
      setValidatedZipStatus(true);
    }
    prevPropsRef.current.zipCode = propsZipCode;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the zipCode prop changes
  }, [propsZipCode]);

  // --- componentDidUpdate: vehicleOverride ---
  useEffect(() => {
    const prevOverride = prevPropsRef.current.vehicleOverride;
    if (vehicleOverride?.style?.id && !isEqualById(prevOverride?.style, vehicleOverride?.style)) {
      onVehicleOverride(vehicleOverride, creativeIdOverride);
    }
    prevPropsRef.current.vehicleOverride = vehicleOverride;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when vehicleOverride prop changes
  }, [vehicleOverride]);

  // --- componentDidUpdate: setAppraiseFinished step tracking ---
  useEffect(() => {
    if (setAppraiseFinished) {
      const activeStep = getActiveStep();
      const isActiveStepFinal = activeStep?.isFinal;
      if (!isActiveStepFinal) {
        const prevSteps = prevStateRef.current.steps || [];
        const prevActiveStep = prevSteps.find(step => step.isActive) || {};
        const isActiveStepChanged = activeStep?.id !== prevActiveStep.id;
        if (isActiveStepChanged) {
          setAppraiseFinished(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when steps or setAppraiseFinished changes; getActiveStep is derived from steps
  }, [state.steps, setAppraiseFinished]);

  // --- componentDidUpdate: vehicle style/year change (updateVehicleWithGtm) ---
  useEffect(() => {
    const { vehicle } = state;
    const prevVehicle = prevStateRef.current?.vehicle;

    if (
      isExtendedReport &&
      !isEmpty(vehicle.style) &&
      !isEmpty(vehicle.year) &&
      (!isEqualById(vehicle?.style, prevVehicle?.style) || vehicle.year?.year !== prevVehicle?.year?.year)
    ) {
      const modelYearId = vehicle?.style?.submodels?.years?.[vehicle.year.year]?.modelYearId;
      updateVehicleWithGtm({ styleId: vehicle.style.id, modelYearId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tracks specific vehicle fields to avoid firing on unrelated vehicle property changes
  }, [state.vehicle?.style?.id, state.vehicle?.year?.year, isExtendedReport, updateVehicleWithGtm]);

  // Update prevStateRef LAST so all effects above can read the old state
  useEffect(() => {
    prevStateRef.current = state;
  });

  // --- Render helper ---
  function renderTradeInAppraiserItem(step, idx, activeStepId) {
    const {
      isVinValid,
      appliedTradeIn,
      isPaymentLimitsLoading,
      isEscapeHatch,
      isVinFromDrawer,
      styleIds,
      vin,
      isLoadingColorsAndOptions,
      steps,
      vehicle,
      mileage,
      isOfferConditionsStepLoaded,
      isLicensePlateAndVinStepLoaded,
      isInstantOfferStepLoaded,
      allConditionTotalPrices,
      condition,
    } = state;

    const totalPrices = getTotalPrices(allConditionTotalPrices, condition);
    const { vin: predefinedVin, isQueryPredefinedVin } = getPredefinedVin(location);

    const nextStepTitle = getStepTitle(steps, step.nextId);
    const transitioningStep = steps.find(({ id }) => id === transitioningStepId);
    const activeStep = steps.find(({ id }) => id === activeStepId);

    return (
      <TradeInAppraiserItem
        id={step.id}
        stepCounter={idx + 1}
        onDone={onDone}
        onContinue={changeStep}
        onSave={saveEstimatedPayments}
        hasTotalPayment={!!totalPrices[TRADE_IN_VALUE]}
        key={step.id}
        isExtendedReport={isExtendedReport}
        isVinValid={isVinValid}
        appliedTradeIn={appliedTradeIn}
        isPaymentLimitsLoading={isPaymentLimitsLoading}
        predefinedVin={predefinedVin}
        isQueryPredefinedVin={isQueryPredefinedVin}
        isVinFromDrawer={isVinFromDrawer}
        styleIds={styleIds}
        isEscapeHatch={isEscapeHatch && !vin}
        isMobile={isMobile}
        isLoadingColorsAndOptions={isLoadingColorsAndOptions}
        nextStepTitle={nextStepTitle}
        isOfferConditionsStepLoaded={isOfferConditionsStepLoaded}
        isLicensePlateAndVinStepLoaded={isLicensePlateAndVinStepLoaded}
        isInstantOfferStepLoaded={isInstantOfferStepLoaded}
        vin={vin}
        scrollToOnApply={scrollToOnApply}
        isEmo4318Chal1={isEmo4318Chal1}
        isHiddenForRenewal={isHiddenForRenewal}
        isStepClosing={transitioningStepId === step.id}
        isStepOpening={isEmo4318Chal1 && step.id === transitioningStep?.nextId}
        isStepOpenDelayed={isEmo4318Chal1 && isNextStepOpenDelayed && step.isActive}
        isNextStepDone={steps.find(({ id }) => id === step.nextId)?.isDone}
        onStepCollapseComplete={onStepCollapseComplete}
        {...step}
        isNavigatingBetweenDoneSteps={step.isDone && activeStep?.isDone}
      >
        <TradeInAppraiserStep
          stepId={step.id}
          activeStepId={activeStepId}
          isMobile={isMobile}
          isExtendedReport={isExtendedReport}
          locationAndStyleProps={getLocationAndStyleData(step, state, props)}
          optionsAndColorsProps={getOptionsAndColorsData(step, state, props, helpers)}
          conditionAndMileageProps={getConditionAndMileageData(step, state, props)}
          appraisalReportProps={getAppraisalReportData(step, state, props, helpers)}
          licensePlateAndVinProps={getLicensePlateAndVinData(step, state, props, helpers)}
          offerConditionsProps={getOfferConditionsData(step, state, props, helpers)}
          emailStepProps={getEmailStepData(step, state)}
          instantOfferProps={getInstantOfferData(step, state, props, helpers)}
          escapeHatchProps={getEscapeHatchData(step, state, props, helpers)}
          isContrastUpdate={isContrastUpdate || isExtendedReport}
          vehicle={vehicle}
          mileage={mileage}
          renewalValues={isRenewal ? renewalValues : null}
          isAppExtensionPage={isAppExtensionPage}
          ReportStep={ReportStep}
          LocationAndStyleStepComponent={LocationAndStyleStepComponent}
        />
      </TradeInAppraiserItem>
    );
  }

  // --- Render ---
  const { steps, isPartnerOfferEligibleVin } = state;
  const { id: activeStepId } = getActiveStep();

  return (
    <div className={className}>
      <TradeInAppraiserHeader
        subHeaderText={subHeaderText}
        headerText={headerText}
        isExtendedReport={isExtendedReport}
      />
      <Row>
        <Col {...columnsSize}>
          <TradeInAppraiserContextProvider helpers={helpers}>
            <div
              className={classnames('trade-in-appraiser pos-r', {
                'sticky-step-header-enabled': isMobile && isEmo4318Chal1,
                'emo-4318-chal1': isEmo4318Chal1,
              })}
              data-testid="trade-in-appraiser"
            >
              <div className="trade-in-appraiser-steps">
                {steps.map((step, idx) => (
                  <Fragment key={`tia-item-${step.id}`}>{renderTradeInAppraiserItem(step, idx, activeStepId)}</Fragment>
                ))}
              </div>
              {shouldRenderTestimonials({ isExtendedReport, activeStepId, isPartnerOfferEligibleVin }) && (
                <AppraisalReportUserTestimonials
                  className={classnames('white-bleed-bg', activeStepId !== INSTANT_OFFER ? 'py-2' : 'pb-2')}
                />
              )}
            </div>
          </TradeInAppraiserContextProvider>
        </Col>
      </Row>
    </div>
  );
}

TradeInAppraiserUI.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  className: PropTypes.string,
  zipCode: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  isMobile: PropTypes.bool,
  onDone: PropTypes.func,
  onTradeInUpdate: PropTypes.func,
  onVehicleUpdate: PropTypes.func,
  updateVehicleWithGtm: PropTypes.func,
  columnsSize: PropTypes.shape({
    xs: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
  }),
  isExtendedReport: PropTypes.bool,
  useGraphqlUsedTmv: PropTypes.bool,
  tmvConditionsResponseData: PropTypes.shape({
    responseId: PropTypes.string,
    tmvConditions: PropTypes.shape({}),
  }),
  estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
  headerText: PropTypes.string,
  subHeaderText: PropTypes.string,
  onEstimatedValuesWidgetHide: PropTypes.func,
  onPartnerFaqQuestionsShow: PropTypes.func,
  setAppraiseFinished: PropTypes.func,
  userEmail: PropTypes.string,
  sellYourCarConfig: sellYourCarConfigParams,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  isUnknownVisitorZip: PropTypes.bool,
  isUsed: PropTypes.bool,
  vehicleOverride: PropTypes.shape({
    make: PropTypes.shape({}),
    model: PropTypes.shape({}),
    year: PropTypes.shape({}),
    style: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  onLinkWidgetHide: PropTypes.func,
  vinStyles: PropTypes.shape({
    results: PropTypes.arrayOf(
      PropTypes.shape({
        styleId: PropTypes.number,
        year: PropTypes.number,
        makeName: PropTypes.string,
        makeNiceId: PropTypes.string,
        modelName: PropTypes.string,
        modelNiceId: PropTypes.string,
      })
    ),
    transmissionType: PropTypes.string,
    exteriorColor: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    interiorColor: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  drawerCreativeId: PropTypes.string,
  modsData: PropTypes.shape({
    mileage: PropTypes.number,
    condition: PropTypes.string,
    transmission: PropTypes.string,
    styleId: PropTypes.number,
    abTestBucketing: PropTypes.arrayOf(PropTypes.string),
    offerFinalizedAt: PropTypes.string,
    vin: PropTypes.string,
    exteriorColorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    interiorColorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        expiresDateUtc: PropTypes.string,
        expiresDisplayDate: PropTypes.string,
      })
    ),
  }),
  isContrastUpdate: PropTypes.bool,
  displayTradeInValues: PropTypes.bool,
  isAppExtensionPage: PropTypes.bool,
  scrollToOnApply: PropTypes.string,
  dma: PropTypes.string,
  ReportStep: PropTypes.node,
  LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
    styleId: PropTypes.string,
  }),
  isHighlightsPage: PropTypes.bool,
  creativeIdOverride: PropTypes.string,
  sellingPrice: PropTypes.number,
  msrp: PropTypes.number,
  residualValue: PropTypes.number,
  isPurchase: PropTypes.bool,
  isEmo4318Chal1: PropTypes.bool,
  isHiddenForRenewal: PropTypes.bool,
  renewalValues: PropTypes.shape({
    mileage: PropTypes.number,
    zipCode: PropTypes.string,
    email: PropTypes.string,
  }),
  renewalModsRecord: PropTypes.shape({ vehicle: PropTypes.shape({}) }),
  emo4379Recipe: PropTypes.string,
};

export const stateToPropsConfig = {
  dma: bindToPath('location.dma', VisitorModel),
  zipCode: bindToPath('location.zipCode', VisitorModel),
  tmvConditionsResponseData: bindToPath(
    ({ allTmvParams, isExtendedReport, useGraphqlUsedTmv }) =>
      allTmvParams
        ? getAppraiserUsedTmvPath({
            styleId: allTmvParams.styleId,
            isExtendedReport,
            useGraphqlUsedTmv,
            withOptions: true,
          })
        : '',
    VehicleModel,
    transformTmvConditions
  ),
  vinStyles: bindToPath(({ location: loc, estimatedAppraisalVin }) => {
    const { vin: urlVin } = getPredefinedVin(loc);
    const vin = urlVin || estimatedAppraisalVin;
    return buildStylesBasicPathFromVin({ vin });
  }, VehicleVinModel),
  modsData: bindToPath(
    ({ location: loc, estimatedAppraisalVin, estimatedVehicle: estVehicle, isExtendedReport: isExtReport }) =>
      isExtReport && !isEmpty(estVehicle)
        ? buildPartnerOfferVehicleInfoPath({
            vin: estimatedAppraisalVin || getQuery(loc).vin,
            modsId: getQuery(loc).modsId,
          })
        : null,
    PartnerOfferModel
  ),
};

export const mapDispatchToProps = dispatch => ({
  updateVehicleWithGtm: ({ styleId, modelYearId }) => dispatch(updateVehicleByStyleId(styleId, modelYearId, true)),
});

export const mapStateToProps = state => ({
  allTmvParams: state.vehicle?.allTmvParams,
  isMobile: state.mobile,
  userEmail: state.profile?.data?.idm?.identifiers?.email?.[0] ?? '',
  location: state.pageContext?.location,
  isUnknownVisitorZip: isUnknownZip(state.visitor?.location),
});

export function TradeInAppraiserEmo4318Wrapper(props) {
  const { isMobile = false, isExtendedReport = false } = props;

  if (!isMobile || !isExtendedReport) {
    return <TradeInAppraiserUI {...props} />;
  }

  return (
    <Experiment name="EMO-4318" showDefault>
      <Recipe name="ctrl" isDefault>
        <TradeInAppraiserUI {...props} />
      </Recipe>
      <Recipe name="chal1">
        <TradeInAppraiserUI {...props} isEmo4318Chal1 />
      </Recipe>
    </Experiment>
  );
}

TradeInAppraiserEmo4318Wrapper.propTypes = {
  isMobile: PropTypes.bool,
  isExtendedReport: PropTypes.bool,
};

export const TradeInAppraiserIdmWrapper = insiderMethods(TradeInAppraiserEmo4318Wrapper);

export const TradeInAppraiser = connect(
  mapStateToProps,
  mapDispatchToProps
)(connectToModel(TradeInAppraiserIdmWrapper, stateToPropsConfig));
